import { Link } from '@servicetitan/anvil2';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import {
    formatCurrency,
    isRenderableBannerPreApproval,
    isSynchronyProvider,
} from '@servicetitan/financing-utils-shared';
import { PreApprovalActivity } from '@servicetitan/financing-api-client/src/prequals-api';
import { PreApprovalStore } from './preApprovalStore';
import { PreApprovalBannerCard } from './PreApprovalBannerCard';
import { useLogPreApprovalActivity } from './preApprovalActivity';
import { preQualifyLink } from './PreApprovalBanner.module.scss';

interface PreApprovalBannerProps {
    customerName?: string;
    className?: string;
    // "See If I Pre-qualify" action: apply now without the on-file pre-approval.
    onPreQualify?: () => void;
}

/**
 * Display-only Select-Plan banner for pre-approval providers that don't render a plan card. GreenSky
 * shows a card via `usePreApprovalDisplay`; banner providers (currently Synchrony) show this instead.
 * The "Not {name}? See If I Pre-qualify" link is always shown, for both homeowners and technicians.
 */
export const PreApprovalBanner = ({
    customerName,
    className,
    onPreQualify,
}: PreApprovalBannerProps) => {
    const { preApproval } = PreApprovalStore.useStore();
    const { setupData, setSetupData } = SetupDataStore.useStore();
    const provider = setupData?.provider;
    const logActivity = useLogPreApprovalActivity(preApproval?.id, setupData?.tenantId);

    if (!isRenderableBannerPreApproval(provider, preApproval)) {
        return null;
    }

    const name = customerName?.trim();

    const handlePreQualify = () => {
        logActivity(PreApprovalActivity.NotMeApply);
        // Applying fresh without the on-file offer — a plain application, not a prequalification.
        setSetupData(prev => ({ ...prev!, preApprovalId: undefined }));
        onPreQualify?.();
    };

    return (
        <PreApprovalBannerCard
            title={getPreApprovalBannerTitle(provider, name, preApproval?.amount)}
            expiresAt={preApproval?.expiresAt}
            className={className}
        >
            <Link appearance="secondary" className={preQualifyLink} onClick={handlePreQualify}>
                {name ? `Not ${name}? See If I Pre-qualify` : 'See If I Pre-qualify'}
            </Link>
        </PreApprovalBannerCard>
    );
};

// Drops the "for up to $X" clause for a missing or 0 amount (Synchrony's real chain hardcodes 0).
export const getPreApprovalBannerTitle = (
    provider: string | null | undefined,
    customerName: string | undefined,
    amount?: number | null,
): string => {
    const amountClause = amount
        ? ` for up to ${formatCurrency(amount, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
          })}`
        : '';
    const providerSuffix = isSynchronyProvider(provider) ? ' for a Synchrony Project Card' : '';
    const offer = `pre-approved${amountClause}${providerSuffix}.`;

    return customerName ? `${customerName}, you're ${offer}` : `You're ${offer}`;
};
